import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class PageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    console.error(`[PageErrorBoundary] ${this.props.pageName ?? "Page"} crashed:`, error, info.componentStack);
    fetch("/api/log-client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page: this.props.pageName ?? "unknown",
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
      }),
    }).catch(() => {});
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center">
          <div className="max-w-lg w-full bg-destructive/10 border border-destructive/30 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">
              {this.props.pageName ?? "Page"} failed to load
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              A rendering error occurred. The details below can help diagnose the issue.
            </p>
            <pre className="text-xs text-left bg-muted rounded p-3 overflow-auto max-h-48 whitespace-pre-wrap break-all mb-3">
              {this.state.error?.message ?? "Unknown error"}
              {"\n\n"}
              {this.state.error?.stack ?? ""}
            </pre>
            {this.state.componentStack && (
              <details className="text-left mb-3">
                <summary className="text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground">
                  Component stack (click to expand)
                </summary>
                <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-48 whitespace-pre-wrap break-all mt-2">
                  {this.state.componentStack}
                </pre>
              </details>
            )}
            <button
              className="mt-1 px-4 py-2 bg-primary text-primary-foreground rounded text-sm hover:bg-primary/90"
              onClick={() => this.setState({ hasError: false, error: null, componentStack: null })}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
